import React, { useMemo } from "react";
import { useProjectContext } from "../layout/ProjectContext";
import {
  useGetManagementDashboardQuery,
  useGetSiteLayoutQuery,
} from "../features/management-dashboard/apiSlice";
import EmptyHint from "../components/EmptyHint";
import SiteLayoutManager from "../features/site-layout/SiteLayoutManager";

const DEVICE_TYPE_LABELS: Record<string, string> = {
  ground_settlement: "Ground Settlement",
  building_settlement: "Building Settlement",
  utility_settlement: "Utility Settlement",
  ground_water: "Ground Water",
  tilting: "Tilting",
  vibration: "Vibration",
  crack: "Crack Gauge",
  movement: "Movement",
  ground_movement: "Ground Movement",
  building_movement: "Building Movement",
  utility_movement: "Utility Movement",
};

const DEVICE_UNITS: Record<string, string> = {
  ground_settlement: "mm",
  building_settlement: "mm",
  utility_settlement: "mm",
  ground_water: "mPD",
  tilting: "rad",
  vibration: "mm/s",
  crack: "mm",
  movement: "mm",
  ground_movement: "mm",
  building_movement: "mm",
  utility_movement: "mm",
};

const DEVICE_ICONS: Record<string, string> = {
  ground_settlement: "bi-arrow-down-square",
  building_settlement: "bi-building",
  utility_settlement: "bi-diagram-3",
  ground_water: "bi-droplet-half",
  crack: "bi-rulers",
  tilting: "bi-triangle",
  vibration: "bi-activity",
  movement: "bi-arrows-move",
  ground_movement: "bi-arrows-move",
  building_movement: "bi-arrows-move",
  utility_movement: "bi-arrows-move",
};

const HIDDEN_DEVICE_TYPES = new Set([
  "ground_settlement",
  "building_settlement",
  "utility_settlement",
]);

const ManagementDashboard: React.FC = () => {
  const { projectId } = useProjectContext();

  const { data: dashboard, isFetching: dashLoading } =
    useGetManagementDashboardQuery(
      { projectId: projectId ?? undefined },
      { skip: projectId == null },
    );
  const { data: siteLayout, isFetching: layoutLoading } = useGetSiteLayoutQuery(
    projectId as number,
    { skip: projectId == null },
  );

  const visibleDeviceTypes = useMemo(
    () =>
      (dashboard?.deviceTypes ?? []).filter(
        (dt) => !HIDDEN_DEVICE_TYPES.has(dt.type),
      ),
    [dashboard],
  );

  const totals = useMemo(() => {
    if (!dashboard) return null;
    const t = {
      alert: 0,
      alarm: 0,
      action1: 0,
      action2: 0,
      action3: 0,
      upperBound: 0,
      lowerBound: 0,
      damaged: 0,
    };
    visibleDeviceTypes.forEach((dt) => {
      t.alert += dt.aaaExceedances.alert;
      t.alarm += dt.aaaExceedances.alarm;
      t.action1 += dt.aaaExceedances.action1;
      t.action2 += dt.aaaExceedances.action2;
      t.action3 += dt.aaaExceedances.action3;
      if (dt.waterLevelExceedances) {
        t.upperBound += dt.waterLevelExceedances.upperBound;
        t.lowerBound += dt.waterLevelExceedances.lowerBound;
      }
      t.damaged += dt.damageReports;
    });
    return t;
  }, [dashboard, visibleDeviceTypes]);

  const suspendedCount = useMemo(() => {
    if (!siteLayout) return 0;
    return siteLayout.devices.filter((d) => !d.recentReading).length;
  }, [siteLayout]);

  if (projectId == null) {
    return (
      <EmptyHint
        icon="bi-folder2-open"
        title="Select a project"
        subtitle="Choose a project from the top bar to see live monitoring data."
      />
    );
  }

  if (dashLoading || layoutLoading) {
    return (
      <EmptyHint
        icon="bi-hourglass-split"
        title="Loading…"
        subtitle="Fetching live data from API."
      />
    );
  }

  if (!dashboard) {
    return (
      <EmptyHint
        icon="bi-exclamation-triangle"
        title="No data"
        subtitle="API returned no dashboard data for this project."
      />
    );
  }

  const alarmCount = totals?.alarm ?? 0;
  const actionCount =
    (totals?.action1 ?? 0) + (totals?.action2 ?? 0) + (totals?.action3 ?? 0);
  const waterAbove = totals?.upperBound ?? 0;
  const waterBelow = totals?.lowerBound ?? 0;
  const waterAbnormal = waterAbove + waterBelow;

  return (
    <>
      {/* Stat row */}
      <div className="grid cols-3 mb-4">
        <div className="stat success">
          <div>
            <div className="label">Checkpoints</div>
            <div className="value">{dashboard.totalDevices}</div>
            <div className="delta text-muted">
              <span style={{ color: "var(--c-danger)", fontWeight: 500 }}>
                {totals?.damaged ?? 0} damaged
              </span>{" "}
              ·{" "}
              <span style={{ color: "var(--c-warning)", fontWeight: 500 }}>
                {suspendedCount} suspended
              </span>
            </div>
          </div>
          <div className="icon">
            <i className="bi bi-cpu" />
          </div>
        </div>
      </div>
      <div className="grid cols-4 mb-4">
        <div className="stat warning">
          <div>
            <div className="label">At alert level</div>
            <div className="value">{totals?.alert ?? 0}</div>
            <div className="delta text-muted">Not yet alarm</div>
          </div>
          <div className="icon">
            <i className="bi bi-exclamation-triangle" />
          </div>
        </div>
        <div className="stat danger">
          <div>
            <div className="label">At alarm level</div>
            <div className="value">{alarmCount}</div>
            <div className="delta text-muted">Alarm level</div>
          </div>
          <div className="icon">
            <i className="bi bi-bell" />
          </div>
        </div>
        <div className="stat danger">
          <div>
            <div className="label">At action level</div>
            <div className="value">{actionCount}</div>
            <div className="delta text-muted">Action 1 / 2 / 3</div>
          </div>
          <div className="icon">
            <i className="bi bi-bell-fill" />
          </div>
        </div>
        <div className={waterAbnormal > 0 ? "stat danger" : "stat success"}>
          <div>
            <div className="label">Abnormal Water Level</div>
            <div className="value">{waterAbnormal}</div>
            <div className="delta text-muted">
              {waterAbove} above max · {waterBelow} below min
            </div>
          </div>
          <div className="icon">
            <i className="bi bi-droplet-half" />
          </div>
        </div>
      </div>

      {/* Site layout + By Type */}
      <div
        className="grid cols-3 mb-4"
        style={{ gridTemplateColumns: "2fr 1fr" }}
      >
        <div className="card">
          <div className="card-head">
            <h2>Site Layout</h2>
          </div>
          {projectId != null ? (
            <SiteLayoutManager mode="view" projectId={projectId} />
          ) : (
            <div className="text-muted" style={{ padding: 24 }}>
              Select a project to view its site layout.
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-head">
            <h2>By Type</h2>
          </div>
          <div className="card-body">
            {visibleDeviceTypes.length === 0 && (
              <div className="text-muted" style={{ padding: 16 }}>
                No device types reported.
              </div>
            )}
            {visibleDeviceTypes.map((dt) => {
              const e = dt.aaaExceedances;
              const isGroundWater = dt.type === "ground_water";

              let statusSummary;
              if (isGroundWater && dt.waterLevelExceedances) {
                const aboveMax = dt.waterLevelExceedances.upperBound;
                const belowMin = dt.waterLevelExceedances.lowerBound;
                statusSummary = (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {aboveMax > 0 && (
                      <span className="badge alarm">{aboveMax} above max</span>
                    )}
                    {belowMin > 0 && (
                      <span className="badge alarm">{belowMin} below min</span>
                    )}
                  </div>
                );
              } else {
                statusSummary = (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {e.alert > 0 && (
                      <span className="badge warn">{e.alert} alert</span>
                    )}
                    {e.alarm > 0 && (
                      <span className="badge alarm">{e.alarm} alarm</span>
                    )}
                    {e.action1 > 0 && (
                      <span className="badge alarm">{e.action1} action1</span>
                    )}
                    {e.action2 > 0 && (
                      <span className="badge alarm">{e.action2} action2</span>
                    )}
                    {e.action3 > 0 && (
                      <span className="badge alarm">{e.action3} action3</span>
                    )}
                  </div>
                );
              }

              return (
                <div
                  key={dt.type}
                  style={{
                    padding: "12px 0",
                    borderBottom: "1px solid var(--c-border)",
                  }}
                >
                  <div className="row gap-3" style={{ marginBottom: 8 }}>
                    <div
                      className="stat-icon"
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: 8,
                        background: "#eff6ff",
                        color: "var(--c-primary)",
                        display: "grid",
                        placeItems: "center",
                      }}
                    >
                      <i
                        className={`bi ${DEVICE_ICONS[dt.type] || "bi-circle"}`}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div className="fw-600">
                        {DEVICE_TYPE_LABELS[dt.type] || dt.type}
                      </div>
                      <div className="text-muted text-sm">
                        {dt.deviceCount} device{dt.deviceCount > 1 ? "s" : ""} ·{" "}
                        {DEVICE_UNITS[dt.type] || ""}
                      </div>
                    </div>
                  </div>
                  {statusSummary}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Recent readings */}
      {/* <div className="card">
        <div className="card-head">
          <h2>Recent Readings</h2>
          <Link to="/reviewer/dashboard" className="btn btn-sm">
            View charts <i className="bi bi-arrow-right" />
          </Link>
        </div>
        <table className="table">
          <thead>
            <tr>
              <th>Device</th>
              <th>Type</th>
              <th>Value</th>
              <th>Time</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {todayReadings.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  className="text-muted"
                  style={{ textAlign: "center", padding: 24 }}
                >
                  No recent readings.
                </td>
              </tr>
            )}
            {todayReadings.map((d) => {
              let badge;
              if (d.alertStatus === "normal") {
                badge = <span className="badge ok">normal</span>;
              } else if (d.alertStatus === "alert") {
                badge = <span className="badge warn">alert</span>;
              } else if (d.alertStatus === "alarm") {
                badge = <span className="badge alarm">alarm</span>;
              } else {
                // action1, action2, action3
                badge = <span className="badge alarm">action</span>;
              }

              const formattedValue = Number(d.recentReading!.value).toFixed(3);

              return (
                <tr key={d.id}>
                  <td className="fw-600">{d.name}</td>
                  <td>{DEVICE_TYPE_LABELS[d.type] || d.type}</td>
                  <td>
                    {formattedValue}
                    {DEVICE_UNITS[d.type] ? ` ${DEVICE_UNITS[d.type]}` : ""}
                  </td>
                  <td>
                    {new Date(d.recentReading!.measurementAt).toLocaleString()}
                  </td>
                  <td>{badge}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div> */}
    </>
  );
};

export default ManagementDashboard;
